import { Link as RouterLink, generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heading, Text } from '@bn-apps/design-system-ui-react';
import { Card } from '@components/common/Card';
import { useAuth } from '@hooks/useAuth';
import { useStatusReportIndex } from '@hooks/useStatusReports';
import { formatPeriod, localizeReport } from '@services/statusReports.service';
import routes from '@config/routes';
import styles from './StatusReports.module.scss';

/**
 * "Siste statusrapport" card for the landing page. Only rendered for
 * authenticated users: the reports are served behind OAuth2, so an anonymous
 * request would be redirected to the sign-in page.
 */
const LatestStatusReportCard = (): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAuthenticated = Boolean(user?.isAuthenticated);
  const { reports, loading, error } = useStatusReportIndex(isAuthenticated);

  if (!isAuthenticated || loading || error || reports.length === 0) return null;
  const latest = localizeReport(reports[0], i18n.language);

  return (
    <section aria-labelledby="latest-status-report" className="bn-mbe-2000">
      <Card color="blue" padding="lg" elevation="overlay">
        <div className={styles.latestCardGrid}>
          <div>
            <Text fontSize="xs" fontWeight="bold" className={styles.latestLabel}>
              {t('statusReports.latestCard.label')} · {formatPeriod(latest.period, i18n.language)}
            </Text>
            <Heading level={2} fontSize="lg" id="latest-status-report" className="bn-mbe-400" lang={latest.lang}>
              {latest.title}
            </Heading>
            {latest.intro && (
              <Text textWrapping="break-normal" className={styles.lead} lang={latest.lang}>
                {latest.intro}
              </Text>
            )}
            <div className={styles.latestCardActions}>
              <RouterLink to={generatePath(routes.statusReportDetail, { period: latest.period })}>
                {t('statusReports.latestCard.readMore')} →
              </RouterLink>
              <RouterLink to={routes.statusReports}>{t('statusReports.latestCard.allReports')}</RouterLink>
            </div>
          </div>
          {latest.highlights.length > 0 && (
            <ul className={styles.highlightList} aria-label={t('statusReports.highlights')} lang={latest.lang}>
              {latest.highlights.map((highlight, i) => (
                <li key={`${highlight.title}-${i}`}>
                  <span className={styles.highlightListEmoji} aria-hidden="true">{highlight.emoji}</span>
                  <span>{highlight.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </section>
  );
};

export default LatestStatusReportCard;
